import { inject, Service } from '@angular/core';
import { EventBusService } from '@shared';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
	PRIVATE_MESSAGE_EVENTS,
	PrivateMessageOrderStateEvent,
	PrivateMessageReceivedEvent,
	PrivateMessageReloadEvent,
	PrivateMessageTextEvent
} from './private-message-events.model';

/**
 * Reads private-message events off the event bus.
 *
 * This is the whole surface a feature needs: subscribe to the stream that
 * concerns it and stay ignorant of SSE, tokens, reconnects and upstream action
 * names. A new consumer is added by injecting this service — never by touching
 * {@link PrivateMessageService}.
 */
@Service()
export class PrivateMessageEventConsumerService {
	private readonly eventBus = inject(EventBusService);

	readonly received$ = this.stream<PrivateMessageReceivedEvent>(PRIVATE_MESSAGE_EVENTS.RECEIVED);
	readonly textReceived$ = this.stream<PrivateMessageTextEvent>(PRIVATE_MESSAGE_EVENTS.TEXT_RECEIVED);
	readonly orderStateChanged$ = this.stream<PrivateMessageOrderStateEvent>(
		PRIVATE_MESSAGE_EVENTS.ORDER_STATE_CHANGED
	);
	readonly ordersReloadRequested$ = this.stream<PrivateMessageReloadEvent>(
		PRIVATE_MESSAGE_EVENTS.ORDERS_RELOAD_REQUESTED
	);
	readonly remainReloadRequested$ = this.stream<PrivateMessageReloadEvent>(
		PRIVATE_MESSAGE_EVENTS.REMAIN_RELOAD_REQUESTED
	);
	readonly depositPortfolioReloadRequested$ = this.stream<PrivateMessageReloadEvent>(
		PRIVATE_MESSAGE_EVENTS.DEPOSIT_PORTFOLIO_RELOAD_REQUESTED
	);

	/**
	 * The producer dispatches fire-and-forget, so the bus holds `undefined`
	 * between events — filtered out here so a late subscriber waits for the next
	 * real one rather than acting on a cleared snapshot.
	 */
	private stream<T>(event: string): Observable<T> {
		return this.eventBus.on$<T>(event).pipe(
			filter(Boolean),
			map(wrapped => wrapped.data)
		);
	}
}
